import { useCallback, useState } from 'react'

import { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { toast } from 'sonner'

import { useNavigation } from '~/hooks/useNavigation'
import { useServerRequests } from '~/hooks/useRequests'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import { captureException } from '~/utils/errors'
import { startPasskeyAuthentication } from '~/utils/passkeys'

/**
 * Step-up check for sensitive actions. Runs the WebAuthn ceremony against the
 * server's confirm endpoints and resolves to whether it succeeded, so callers
 * gate on the result: `if (!(await confirmWithPasskey())) return`.
 *
 * Enforcement is client-side only today; the server does not record the
 * confirmation.
 */
export const usePasskeyConfirmation = () => {
  const { serverGetRequest, serverPostRequest } = useServerRequests()
  // The confirm endpoint accepts any user passkey, including non-org signers.
  const { passkeys } = useUserPasskeys()
  const hasPasskey = passkeys.length > 0
  const navigate = useNavigation()
  const [isConfirming, setIsConfirming] = useState(false)

  const confirmWithPasskey = useCallback(async (): Promise<boolean> => {
    if (!hasPasskey) {
      toast.error('No passkey found', {
        description: 'Add a passkey to continue',
        action: {
          label: 'Settings',
          onClick: () => navigate('/settings/personal/signing-keys'),
        },
      })
      return false
    }

    setIsConfirming(true)
    try {
      const options = await serverGetRequest<{
        options: PublicKeyCredentialRequestOptionsJSON
      }>({
        path: '/api/passkey/confirm/options',
      })
      if (!options) return false

      const attResp = await startPasskeyAuthentication(options.data.options, {
        onError: ({ error, isNotAllowedError, isUnsupportedError }) => {
          if (isUnsupportedError) {
            toast.error('Browser does not support passkeys', {
              description:
                error instanceof Error ? error.message : String(error),
            })
          } else if (isNotAllowedError) {
            toast.error('Passkey verification cancelled')
          } else {
            toast.error('Failed to verify passkey')
            captureException(error)
          }
        },
      })
      if (!attResp) return false

      const verifyResponse = await serverPostRequest<{ verified: boolean }>({
        path: '/api/passkey/confirm/verify',
        params: {
          credentialId: attResp.id,
          authentication: JSON.stringify(attResp),
        },
        errorMessage: 'Failed to verify passkey',
      })
      return verifyResponse?.data?.verified === true
    } catch (error) {
      captureException(error)
      return false
    } finally {
      setIsConfirming(false)
    }
  }, [hasPasskey, navigate, serverGetRequest, serverPostRequest])

  return { confirmWithPasskey, isConfirming }
}
